// packages
const { h, Component } = require('preact');
import { select, scaleBand, scaleLinear, axisLeft } from 'd3';
import { formatNumberPct, formatYAxisTicks } from '../utils/utils';

const COLORS = {
  republican: '#f2593b',
  democratic: '#1755c5',
  didnotvote: '#99a0af',
};

const CATEGORIES = ['republican', 'democratic', 'didnotvote'];

class PartyBarChart extends Component {
  constructor() {
    super();
    this.state = {};
    this.drawChart = this.drawChart.bind(this);
    this.redrawChart = this.redrawChart.bind(this);
  }

  drawChart() {
    const { yearData, statewideMax } = this.props;
    const container = select(`.party-bar-container-${yearData.year}`);
    if (!container.node()) return;

    const width = container.node().clientWidth;
    const margin = { t: 25, r: 10, b: 65, l: 40 };
    const chartWidth = width - margin.l - margin.r;
    const chartHeight = 220;
    const height = chartHeight + margin.t + margin.b;

    const xScale = scaleBand()
      .domain(CATEGORIES)
      .range([0, chartWidth])
      .padding(0.3);

    const yScale = scaleLinear()
      .domain([0, statewideMax])
      .range([chartHeight, 0]);

    const svg = select(`.party-bar-container-${yearData.year}`)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.l},${margin.t})`);

    // y-axis
    svg.append('g')
      .attr('class', 'axis axis--y')
      .call(
        axisLeft(yScale)
          .tickValues([0, 5000000, 10000000, 15000000])
          .tickSize(-chartWidth)
          .tickFormat(d => formatYAxisTicks(d))
      );

    // bars
    const grouped = CATEGORIES.map(cat => ({ category: cat, value: +yearData[cat] }));

    svg.selectAll('.party-bar')
      .data(grouped)
      .enter()
      .append('rect')
      .attr('class', 'party-bar')
      .attr('x', d => xScale(d.category))
      .attr('y', d => yScale(d.value))
      .attr('width', xScale.bandwidth())
      .attr('height', d => chartHeight - yScale(d.value))
      .attr('fill', d => COLORS[d.category]);

    // percentage labels above bars
    svg.selectAll('.pct-label')
      .data(grouped)
      .enter()
      .append('text')
      .attr('class', 'pct-label')
      .attr('x', d => xScale(d.category) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(d.value) - 12)
      .attr('text-anchor', 'middle')
      .text(d => formatNumberPct(d.value / yearData.registered_voters * 100));

    // party labels below x-axis
    const labelG = svg.append('g').attr('transform', `translate(0,${chartHeight})`);

    ['republican', 'democratic'].forEach(cat => {
      const cx = xScale(cat) + xScale.bandwidth() / 2;
      const g = labelG.append('g').attr('transform', `translate(${cx},20)`);
      g.append('circle')
        .attr('r', 10)
        .attr('fill', COLORS[cat]);
      g.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', 4)
        .attr('fill', 'white')
        .style('font-weight', 'bold')
        .style('font-size', '11px')
        .text(cat[0].toUpperCase());
    });

    const dvX = xScale('didnotvote') + xScale.bandwidth() / 2;
    const dvG = labelG.append('g').attr('transform', `translate(${dvX},20)`);
    dvG.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', 0)
      .attr('fill', '#99a0af')
      .style('font-size', '12px')
      .text('Did not');
    dvG.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', 14)
      .attr('fill', '#99a0af')
      .style('font-size', '12px')
      .text('vote');
  }

  redrawChart() {
    select(`.party-bar-container-${this.props.yearData.year}`)
      .select('svg')
      .remove();
    this.drawChart();
  }

  componentDidMount() {
    this.drawChart();
    window.addEventListener('resize', () => this.redrawChart());
  }

  componentDidUpdate(prevProps) {
    if (prevProps.yearData !== this.props.yearData) {
      this.redrawChart();
    }
  }

  render() {
    const { yearData } = this.props;
    const totalPct = formatNumberPct(
      (yearData.republican + yearData.democratic) / yearData.registered_voters * 100
    );
    return (
      <div class="party-chart-wrapper">
        <div class="party-chart-header">
          <p class="party-chart-label">{yearData.label} primary</p>
          <h2 class="party-chart-year">{yearData.year}</h2>
          <p class="party-chart-total">{totalPct} of {(yearData.registered_voters / 1e6).toFixed(1)}M registered voters</p>
        </div>
        <div class={`party-bar-container-${yearData.year}`}></div>
      </div>
    );
  }
}

export default PartyBarChart;
